import React, { useEffect, useState } from 'react';
import { Check, X, Mail } from 'lucide-react';
import * as invitationsApi from '../../api/invitations';

/**
 * People asking you to come and play.
 *
 * This one acts in place, which NotificationsPanel deliberately does not. That
 * panel sends you to the page that owns the decision, because the accept and
 * reject buttons already live there — but an invitation has no such page. You
 * are not in the league yet, so there is nowhere to send you and nothing you
 * could do when you arrived. The buttons have to be here.
 *
 * **Accepting is different from declining, though: it creates the page.** The moment
 * you are in, the league exists as somewhere to go, and leaving people on the landing
 * page with one fewer row was the whole answer they got — no table, no members, no sign
 * that anything had happened. So an accept navigates, using the leagueId the server
 * reports back. A decline stays put: there is nothing to see.
 *
 * Quiet like its neighbour: nothing waiting renders nothing at all, and a failed
 * read is swallowed rather than leading a landing page with an error about a
 * sidebar.
 *
 * Answered rows are dropped from local state immediately rather than re-fetching
 * — the answer is already known, and a round trip would leave the row sitting
 * there looking unanswered.
 */
function InvitationsPanel({ onAnswered, onOpenLeague, onOpenGame }) {
  const [invitations, setInvitations] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let live = true;
    invitationsApi.getMine()
      .then((rows) => {
        if (live) setInvitations(rows || []);
      })
      .catch(() => {
        if (live) setInvitations([]);
      });
    return () => {
      live = false;
    };
  }, []);

  const answer = async (invitation, action) => {
    setError('');
    setBusyId(invitation.id);
    try {
      const result = await action(invitation.id);
      setInvitations((prev) => prev.filter((i) => i.id !== invitation.id));
      if (onAnswered) onAnswered();
      return result;
    } catch (err) {
      setError(err.name === 'ApiError' ? err.body?.error || 'That did not work' : 'Something went wrong');
      return null;
    } finally {
      setBusyId(null);
    }
  };

  /**
   * Land on the thing you just joined. The league wins when there is one, because it
   * is the season and the game is one cycle of it — that is also true of a GAME
   * invitation inside a league, which enrols you in the league as well. A standalone
   * game has no league, so the game is the destination.
   */
  const accept = async (invitation) => {
    const result = await answer(invitation, invitationsApi.accept);
    if (!result) return;
    if (result.leagueId && onOpenLeague) {
      onOpenLeague(result.leagueId);
      return;
    }
    if (result.scopeType === 'GAME' && result.scopeId && onOpenGame) {
      onOpenGame(result.scopeId);
    }
  };

  if (invitations.length === 0) return null;

  return (
    <div className="invitations-panel">
      <h2 className="notifications-heading">Invitations</h2>
      {error && <p className="error-text">{error}</p>}

      <div className="invitation-list">
        {invitations.map((invitation) => (
          <div key={invitation.id} className="invitation-row">
            <div className="invitation-who">
              <span className="invitation-name">
                <Mail size={15} /> {invitation.scopeName}
              </span>
              <span className="invitation-detail">{describe(invitation)}</span>
            </div>
            {/* Labelled, not icon-only. Two 1rem/2rem-padded buttons with a glyph in
                them and nothing else overflowed the row on a phone and gave no clue
                which was which - the label was in an aria-label only. */}
            <div className="invitation-actions">
              <button
                type="button"
                className="invitation-accept"
                disabled={busyId === invitation.id}
                onClick={() => accept(invitation)}
                aria-label={`Accept ${invitation.scopeName}`}
              >
                <Check size={16} />
                Accept
              </button>
              <button
                type="button"
                className="invitation-decline"
                disabled={busyId === invitation.id}
                onClick={() => answer(invitation, invitationsApi.decline)}
                aria-label={`Decline ${invitation.scopeName}`}
              >
                <X size={16} />
                Decline
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * A game inside a league is worth spelling out: accepting puts you in the league
 * as well, and finding that out afterwards is the surprise this whole area of
 * the app has been getting wrong.
 */
function describe(invitation) {
  const who = `${invitation.inviterName} invited you`;
  if (invitation.scopeType === 'MINI_LEAGUE') {
    return `${who} to this mini-league — you'll be in every tournament it runs`;
  }
  if (invitation.leagueName) {
    return `${who} to this game, which is run by ${invitation.leagueName} — joining puts you in the mini-league too`;
  }
  return `${who} to this game`;
}

export default InvitationsPanel;
